import { Suspense, useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BookMarked, CircleUser, Clock, LayoutList, LogOut, Mail, MapPin, Menu as MenuIcon, Phone, Search, Settings2, UserPlus, WifiOff } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { fullName } from '@/lib/format';
import { useOnline } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import { PUBLIC_NAV } from '@/lib/nav/public';
import { Avatar } from '@/components/ui/misc';
import { Button, ButtonLink, IconButton } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Dialog';
import { Menu } from '@/components/ui/Menu';
import { Skeleton } from '@/components/ui/Skeleton';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

interface LibraryInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  hours: { day: string; hours: string }[];
}

const WEEKDAYS: { key: string; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

/** Reads the real `GET /opac/library` payload ({ name, library, hours }) and also the older flat shape. */
function toLibraryInfo(payload: unknown): LibraryInfo | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const r = payload as Record<string, unknown>;
  if (typeof r.name !== 'string') {
    return null;
  }
  const lib = (r.library && typeof r.library === 'object' ? r.library : {}) as Record<string, unknown>;
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);
  const address = str(r.address) ?? [lib.address1, lib.address2, lib.city, lib.state, lib.postalCode].filter((v): v is string => typeof v === 'string' && v.trim().length > 0).join(', ');
  const flat = Array.isArray(r.hours) ? r.hours.filter((h): h is { day: string; hours: string } => Boolean(h) && typeof (h as { day?: unknown }).day === 'string' && typeof (h as { hours?: unknown }).hours === 'string') : [];
  let hours = flat;
  const opening = (lib.openingHours ?? (r.hours as { openingHours?: unknown } | null)?.openingHours) as Record<string, { open: string; close: string }[] | undefined> | null | undefined;
  if (hours.length === 0 && opening && typeof opening === 'object') {
    hours = WEEKDAYS.map(({ key, label }) => {
      const intervals = opening[key] ?? [];
      return { day: label, hours: intervals.length ? intervals.map((i) => `${i.open} - ${i.close}`).join(', ') : 'Closed' };
    });
  }
  return { name: r.name, address: address || undefined, phone: str(r.phone) ?? str(lib.phone), email: str(r.email) ?? str(lib.email), hours };
}

function useLibraryInfo(): { info: LibraryInfo | null; loading: boolean } {
  const { data, isLoading } = useQuery({
    // Not `opacKeys.custom('library-info')`: the register and event pages cache the raw `/opac/library` reply under that key.
    queryKey: ['opac', 'library-info-shell'],
    staleTime: 30 * 60_000,
    retry: false,
    queryFn: ({ signal }) => api.get<unknown>('/opac/library', { anonymous: true, signal }).then(toLibraryInfo),
  });
  return { info: data ?? null, loading: isLoading };
}

function HeaderSearch({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search');
  };
  return (
    <form role="search" onSubmit={onSubmit} className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label={t('nav.search')}
        placeholder={t('opac.searchPlaceholder')}
        className="h-10 w-full rounded-full border border-line-strong bg-surface pl-10 pr-4 text-sm text-ink shadow-card transition placeholder:text-ink-subtle hover:border-sapphire-400 focus-visible:border-sapphire-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sapphire-500/40 focus-visible:ring-offset-0"
      />
    </form>
  );
}

function AccountMenu() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  if (status !== 'authenticated' || !user) {
    return (
      <div className="flex items-center gap-1.5">
        <ButtonLink to="/login" variant="ghost" size="sm" className="hidden sm:inline-flex">
          {t('nav.signIn')}
        </ButtonLink>
        <ButtonLink to="/register" size="sm" className="hidden sm:inline-flex">
          {t('nav.register')}
        </ButtonLink>
        <Link to="/register" aria-label={t('nav.register')} title={t('nav.register')} className="grid h-10 w-10 place-items-center rounded-lg bg-sapphire-700 text-white transition hover:bg-sapphire-800 sm:hidden">
          <UserPlus className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
        </Link>
      </div>
    );
  }
  const name = fullName(user) || user.cardNumber;
  return (
    <Menu
      label={name}
      header={
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{name}</p>
          <p className="truncate font-mono text-xs text-ink-muted">{user.cardNumber}</p>
        </div>
      }
      items={[
        { id: 'account', label: t('nav.account'), icon: <CircleUser />, onSelect: () => navigate('/account') },
        { id: 'loans', label: t('account.loans'), icon: <BookMarked />, onSelect: () => navigate('/account/loans') },
        { id: 'holds', label: t('account.holds'), icon: <LayoutList />, onSelect: () => navigate('/account/holds') },
        { id: 'prefs', label: t('account.preferences'), icon: <Settings2 />, onSelect: () => navigate('/account/preferences') },
        ...(user.isStaff ? [{ id: 'staff', label: t('nav.staffConsole'), icon: <Settings2 />, onSelect: () => navigate('/staff') }] : []),
        { type: 'separator' as const },
        { id: 'out', label: t('nav.signOut'), icon: <LogOut />, danger: true, onSelect: () => void logout().then(() => navigate('/')) },
      ]}
      trigger={
        <button type="button" aria-label={name} className="flex items-center gap-2 rounded-full p-0.5 pr-2 transition hover:bg-sunken">
          <Avatar name={name} src={user.photoUrl} size="sm" />
          <span className="hidden max-w-[8rem] truncate text-sm font-medium text-ink md:inline">{user.firstName ?? user.surname}</span>
        </button>
      }
    />
  );
}

function Footer() {
  const { t } = useI18n();
  const { info, loading } = useLibraryInfo();
  return (
    <footer className="theme-dark mt-16 bg-midnight text-navy-200">
      <div className="mx-auto grid max-w-page gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
        <div>
          <Logo tone="onDark" size={36} />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-navy-300">{info?.name ?? t('opac.heroSubtitle')}</p>
        </div>
        <div>
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-white">
            <Clock className="h-4 w-4 text-sapphire-300" aria-hidden="true" />
            {t('footer.hours')}
          </h2>
          {loading ? (
            <div className="mt-3 space-y-2" aria-hidden="true">
              <Skeleton className="h-3 w-48 bg-white/10" />
              <Skeleton className="h-3 w-40 bg-white/10" />
            </div>
          ) : info && info.hours.length > 0 ? (
            <dl className="mt-3 space-y-1.5 text-sm">
              {info.hours.map((h) => (
                <div key={h.day} className="flex justify-between gap-6">
                  <dt className="text-navy-300">{h.day}</dt>
                  <dd className="tabular text-white">{h.hours}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-3 text-sm text-navy-300">Opening hours will appear here once the library publishes them.</p>
          )}
        </div>
        <div>
          <h2 className="font-display text-base font-semibold text-white">{t('footer.contact')}</h2>
          <ul className="mt-3 space-y-2 text-sm text-navy-300">
            {info?.address && (
              <li className="flex gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sapphire-300" aria-hidden="true" />
                {info.address}
              </li>
            )}
            {info?.phone && (
              <li className="flex gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-sapphire-300" aria-hidden="true" />
                {info.phone}
              </li>
            )}
            {info?.email && (
              <li className="flex gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sapphire-300" aria-hidden="true" />
                <a className="underline-offset-2 hover:text-white hover:underline" href={`mailto:${info.email}`}>
                  {info.email}
                </a>
              </li>
            )}
            <li>
              <Link to="/staff" className="text-xs text-navy-400 underline-offset-2 hover:text-white hover:underline">
                {t('nav.staffConsole')}
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-navy-400">
        &copy; {new Date().getFullYear()} Lumina. {t('footer.rights')}
      </div>
    </footer>
  );
}

/** Public OPAC frame: elegant top navigation, prominent search, account menu, EN/HI switcher and an informative footer. */
export function PublicShell() {
  const { t } = useI18n();
  const location = useLocation();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => {
    setMenuOpen(false);
    const main = document.getElementById('main');
    // Start at the top of the new page, unless the page already placed focus inside itself (e.g. the scan field).
    if (main && !main.contains(document.activeElement)) {
      main.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <a href="#main" className="skip-link">
        {t('shell.skipToContent')}
      </a>
      <header className="glass sticky top-0 z-30 border-b border-line">
        <div className="mx-auto flex h-16 max-w-page items-center gap-3 px-4 sm:px-6 lg:px-8">
          <IconButton label={t('shell.openMenu')} className="lg:hidden" icon={<MenuIcon className="h-5 w-5" aria-hidden="true" />} onClick={() => setMenuOpen(true)} />
          <Link to="/" aria-label="Lumina home" className="rounded-lg">
            <Logo size={34} />
          </Link>
          <nav aria-label={t('nav.main')} className="ml-4 hidden items-center gap-0.5 lg:flex">
            {PUBLIC_NAV.filter((i) => i.id !== 'home').map((item) => (
              <NavLink key={item.id} to={item.to} end={item.end} className={({ isActive }) => cn('relative rounded-lg px-3 py-2 text-sm font-medium transition-colors', isActive ? 'text-sapphire-800 dark:text-sapphire-200' : 'text-ink-muted hover:text-ink')}>
                {({ isActive }) => (
                  <>
                    {t(item.labelKey)}
                    {isActive && <span className="absolute inset-x-3 -bottom-[1.1rem] h-0.5 rounded-full bg-sapphire-600 dark:bg-sapphire-400" aria-hidden="true" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          {!isHome && <HeaderSearch className="ml-auto hidden max-w-sm flex-1 md:block lg:ml-4" />}
          <div className={cn('flex min-w-0 items-center gap-1', isHome ? 'ml-auto' : 'md:ml-0 ml-auto')}>
            <div className="hidden items-center gap-1 sm:flex">
              <LanguageSwitcher compact />
              <ThemeToggle />
            </div>
            <AccountMenu />
          </div>
        </div>
      </header>

      {!online && (
        <div role="status" className="flex items-center justify-center gap-2 bg-warning-50 px-4 py-2 text-sm font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-500">
          <WifiOff className="h-4 w-4" aria-hidden="true" />
          {t('shell.offline')}
        </div>
      )}

      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        <Suspense fallback={<div className="mx-auto max-w-page space-y-4 px-4 py-10"><Skeleton className="h-10 w-72" /><Skeleton className="h-64 rounded-card" /></div>}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />

      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" size="sm" title="Lumina" description={t('nav.publicCatalogue')}>
        <HeaderSearch className="mb-4" />
        <nav aria-label={t('nav.main')}>
          <ul className="space-y-1">
            {PUBLIC_NAV.map((item) => (
              <li key={item.id}>
                <NavLink to={item.to} end={item.end} onClick={() => setMenuOpen(false)} className={({ isActive }) => cn('flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium', isActive ? 'bg-sapphire-50 text-sapphire-800 dark:bg-sapphire-900/50 dark:text-sapphire-100' : 'text-ink-muted hover:bg-sunken')}>
                  <item.icon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mt-6 flex items-center gap-2 sm:hidden">
          <LanguageSwitcher compact />
          <ThemeToggle />
        </div>
        <div className="mt-6 flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setMenuOpen(false)}>
            {t('common.close')}
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
